// CNC 파트너 — 설정 (담당자 · 각인 옵션 · 알림 · 제조사 에이전트) (2026-10-23, UI 구조 단계)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, UserPlus, HardDriveDownload, ShieldCheck, Download, Lock } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { SAMPLE_MANAGERS } from './partnerSample';
import { useIsMobile, SampleNotice, ui } from './partnerUi';

const NOTI_ITEMS = [
    { key: 'new', label: '신규 케이스 도착' },
    { key: 'due', label: '납기 임박 (D-1)' },
    { key: 'jigFail', label: '지그 생성 실패' },
    { key: 'unshipped', label: '출고 미확인 (매일 아침)' },
    { key: 'quota', label: '지그 묶음 소진 임박' },
];

function Switch({ on, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => onChange(!on)}
            style={{ ...styles.switch, backgroundColor: on ? '#6366f1' : '#cbd5e1' }}
        >
            <span style={{ ...styles.knob, transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
        </button>
    );
}

function PartnerSettings() {
    const mobile = useIsMobile();
    const navigate = useNavigate();
    const [managers, setManagers] = useState(SAMPLE_MANAGERS);
    const [engrave, setEngrave] = useState('id');      // id(케이스ID만) / logo(로고+ID)
    const [noti, setNoti] = useState({ new: true, due: true, jigFail: true, unshipped: true, quota: true });

    return (
        <div>
            <PageHeader icon={Settings} title={'설정'} subtitle={'담당자 · 각인 옵션 · 알림을 설정합니다.'} />
            <div style={mobile ? ui.bodyMobile : ui.body}>
                <SampleNotice />

                {/* 자격 서류 — 맞춤 지대주 등 의료기기 규제 대응(문헌 조사 10-23). 서류 등록·심사는 기존 VendorRegister 화면 */}
                <section style={styles.card}>
                    <div style={styles.cardHead}>
                        <span style={{ ...ui.cardTitle, display: 'inline-flex', alignItems: 'center', gap: 6 }}><ShieldCheck size={16} color="#047857" />{'자격 서류'}</span>
                        <span style={{ ...ui.badge, color: '#047857', backgroundColor: '#d1fae5' }}>{'승인됨'}</span>
                    </div>
                    <div style={styles.row}><span style={styles.rowTitle}>{'자격'}</span><span>{'의료기기 제조업 허가 · 제0000호(샘플)'}</span></div>
                    <div style={styles.row}><span style={styles.rowTitle}>{'GMP 유효기간'}</span><span style={{ color: '#b45309', fontWeight: 600 }}>{'2027-03-31 (만료 160일 전)'}</span></div>
                    <div style={styles.row}><span style={styles.rowTitle}>{'프리밀드 블랭크'}</span><span>{'사용 — 출고 시 허가번호·LOT 기록'}</span></div>
                    <div>
                        <button type="button" style={ui.ghostBtn} onClick={() => navigate('/settings/outsource-vendor')}>{'서류 보기·갱신'}</button>
                    </div>
                    <p style={ui.cardSub}>{'만료가 가까우면 알림이 가고, 만료되면 새 케이스를 받을 수 없습니다.'}</p>
                </section>

                {/* 담당자 */}
                <section style={styles.card}>
                    <div style={styles.cardHead}>
                        <span style={ui.cardTitle}>{'담당자'}</span>
                        <button type="button" style={ui.ghostBtn} onClick={() => alert('미리보기: 담당자 추가는 서버 연결 후 동작합니다.')}>
                            <UserPlus size={14} />{'추가'}
                        </button>
                    </div>
                    {managers.map((m) => (
                        <div key={m.id} style={styles.row}>
                            <div style={{ minWidth: 0 }}>
                                <div style={styles.rowTitle}>{m.name}<span style={styles.role}>{m.role}</span></div>
                                <div style={ui.muted}>{m.phone}</div>
                            </div>
                            <div style={styles.rowRight}>
                                <span style={ui.muted}>{'알림 받기'}</span>
                                <Switch on={m.notify} onChange={(v) => setManagers((p) => p.map((x) => (x.id === m.id ? { ...x, notify: v } : x)))} />
                            </div>
                        </div>
                    ))}
                </section>

                {/* 각인 옵션 */}
                <section style={styles.card}>
                    <span style={ui.cardTitle}>{'각인 옵션'}</span>
                    <p style={ui.cardSub}>{'지그에 새기는 내용입니다. 케이스ID는 항상 들어갑니다.'}</p>
                    <div style={styles.radioRow}>
                        {[
                            { v: 'id', label: '케이스ID만', ex: 'MF-261023-001' },
                            { v: 'logo', label: '로고 + 케이스ID', ex: '[LOGO] MF-261023-001' },
                        ].map((o) => (
                            <label key={o.v} style={{ ...styles.radio, ...(engrave === o.v ? styles.radioOn : {}) }}>
                                <input type="radio" name="engrave" checked={engrave === o.v} onChange={() => setEngrave(o.v)} />
                                <div>
                                    <div style={styles.rowTitle}>{o.label}</div>
                                    <div style={styles.mono}>{o.ex}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </section>

                {/* 알림 */}
                <section style={styles.card}>
                    <span style={ui.cardTitle}>{'알림'}</span>
                    {NOTI_ITEMS.map((n) => (
                        <div key={n.key} style={styles.row}>
                            <span style={styles.rowTitle}>{n.label}</span>
                            <Switch on={noti[n.key]} onChange={(v) => setNoti((p) => ({ ...p, [n.key]: v }))} />
                        </div>
                    ))}
                </section>

                {/* 내 데이터 — 데이터는 제조사 소유, 언제든 전체 내보내기(문헌 조사 10-23: 데이터 잠금은 역효과) */}
                <section style={styles.card}>
                    <span style={{ ...ui.cardTitle, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Lock size={16} color="#475569" />{'내 데이터'}</span>
                    <p style={ui.cardSub}>{'케이스·명세서·단가표·미수금·영업업체 기록은 우리 회사 소유입니다. 언제든 전체를 내려받을 수 있고, 다른 제조사에게는 보이지 않습니다.'}</p>
                    <div>
                        <button type="button" style={ui.ghostBtn} onClick={() => alert('미리보기: 전체 내보내기(엑셀 묶음)는 서버 연결 후 동작합니다.')}>
                            <Download size={14} />{'전체 데이터 내보내기'}
                        </button>
                    </div>
                </section>

                {/* 제조사 에이전트 */}
                <section style={styles.card}>
                    <span style={ui.cardTitle}>{'제조사 에이전트'}</span>
                    <p style={ui.cardSub}>{'PC에 설치하면 새 케이스 파일이 CAM 폴더로 자동으로 내려옵니다. 웹에서 다운로드를 누를 필요가 없습니다.'}</p>
                    <div>
                        <button type="button" style={{ ...ui.ghostBtn, opacity: 0.6, cursor: 'not-allowed' }} disabled>
                            <HardDriveDownload size={14} />{'에이전트 다운로드 (준비 중)'}
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}

const styles = {
    card: { ...ui.card, display: 'flex', flexDirection: 'column', gap: 10 },
    cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    row: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '10px 0', borderTop: '1px solid #f1f5f9',
    },
    rowTitle: { fontSize: 14, fontWeight: 600, color: '#1e293b' },
    role: { marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#64748b' },
    rowRight: { display: 'flex', alignItems: 'center', gap: 8 },
    radioRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    radio: {
        flex: '1 1 200px', display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12,
        border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer',
    },
    radioOn: { borderColor: '#6366f1', backgroundColor: '#fafaff' },
    mono: { fontSize: 12, color: '#64748b', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 4 },
    switch: {
        position: 'relative', width: 44, height: 24, borderRadius: 999, border: 'none',
        cursor: 'pointer', padding: 2, transition: 'background-color .15s', flexShrink: 0,
    },
    knob: {
        display: 'block', width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'transform .15s',
    },
};

export default PartnerSettings;
